import { apiService } from '@/lib/api';
import { getAppOrigin, setStorageItem } from '@/lib/capacitor';
import type { PaymentProviderStatusDTO } from '@/types';

/** Storage keys read back by StripeCallbackPage after Stripe redirects to /payment-callback. */
export const STRIPE_OAUTH_COMPANY_KEY = 'stripe_oauth_company';
export const STRIPE_OAUTH_RETURN_KEY = 'stripe_oauth_return';
export const STRIPE_DEFAULT_RETURN = '/payment-provider';

/**
 * Leaves the app for Stripe Connect OAuth. The state Stripe hands back is opaque (HMAC-signed), so
 * the company and the page to come back to are remembered locally. Always writes the return key,
 * so an abandoned attempt from one page can't redirect a later one from another.
 */
export async function startStripeConnect(companyId: string, returnTo = STRIPE_DEFAULT_RETURN) {
    await setStorageItem(STRIPE_OAUTH_COMPANY_KEY, companyId);
    await setStorageItem(STRIPE_OAUTH_RETURN_KEY, returnTo);
    const response = await apiService.initiateStripeConnect(`${getAppOrigin()}/payment-callback`);
    if (!response.authorize_url) throw new Error('No authorize URL received');
    window.location.href = response.authorize_url;
}

export type ConnectState =
    | 'none'        // No Stripe account connected
    | 'pending'     // Account connected, onboarding incomplete
    | 'active'      // Account fully active
    | 'restricted'; // Account is restricted

/** Derive a single UI state from the provider status DTO. */
export function deriveConnectState(status?: PaymentProviderStatusDTO): ConnectState {
    if (!status || !status.connected) return 'none';

    const onboarding = status.onboarding_status?.toLowerCase();
    if (onboarding === 'restricted') return 'restricted';
    if (onboarding === 'active') return 'active';
    return 'pending';
}
